import React, { FormEvent, useCallback, useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import { Button } from '../../components/common/Button';
import { Modal } from '../../components/modals/Modal';
import { APP_PERMISSIONS } from '../../constants';
import { usePermissions } from '../../hooks/usePermissions';
import {
  createUser,
  getUsers,
  setUserActive,
  updateUser,
  UserCreate,
} from '../../services/usersService';
import { User, UserRole } from '../../types';

const PAGE_SIZE = 20;
const ROLE_OPTIONS = Object.values(UserRole);

interface UserFormState {
  username: string;
  email: string;
  full_name: string;
  password: string;
  role: UserRole;
}

const emptyForm: UserFormState = {
  username: '',
  email: '',
  full_name: '',
  password: '',
  role: UserRole.TEACHER,
};

const formatRole = (role: UserRole) =>
  role
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getAssignableRoles = (currentUser?: User | null): UserRole[] => {
  if (!currentUser) return [];
  if (currentUser.is_superuser || currentUser.role === UserRole.SUPER_ADMIN) {
    return ROLE_OPTIONS;
  }
  if (currentUser.role === UserRole.DISTRICT_ADMIN) {
    return ROLE_OPTIONS.filter(
      role => ![UserRole.SUPER_ADMIN, UserRole.STATE_VIEWER].includes(role)
    );
  }
  if (currentUser.role === UserRole.SCHOOL_ADMIN) {
    return [
      UserRole.PRINCIPAL,
      UserRole.ASSISTANT_PRINCIPAL,
      UserRole.EVALUATOR,
      UserRole.TEACHER,
      UserRole.PARENT,
    ];
  }
  if (currentUser.role === UserRole.PRINCIPAL) {
    return [
      UserRole.ASSISTANT_PRINCIPAL,
      UserRole.EVALUATOR,
      UserRole.TEACHER,
      UserRole.PARENT,
    ];
  }
  return [currentUser.role];
};

export const UsersPage: React.FC = () => {
  const { user: currentUser, hasPermission } = usePermissions();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [activeFilter, setActiveFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form, setForm] = useState<UserFormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [changingStatusId, setChangingStatusId] = useState<number | null>(null);

  const canCreate = hasPermission(APP_PERMISSIONS.CREATE_USER);
  const canUpdate = hasPermission(APP_PERMISSIONS.UPDATE_USER);
  const assignableRoles = getAssignableRoles(currentUser);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUsers({
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        search: search.trim() || undefined,
        role: role || undefined,
        is_active:
          activeFilter === 'all' ? undefined : activeFilter === 'active',
      });
      setUsers(response.data);
      setTotalPages(Math.max(response.pagination.pages, 1));
      setTotalUsers(response.pagination.total);
    } catch (loadError) {
      console.error('Failed to load users:', loadError);
      setUsers([]);
      setError('Users could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, page, role, search]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (selectedUser: User) => {
    setEditingUser(selectedUser);
    setForm({
      username: selectedUser.username,
      email: selectedUser.email,
      full_name: selectedUser.full_name,
      password: '',
      role: selectedUser.role,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setEditingUser(null);
    setForm(emptyForm);
  };

  const submitUser = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.username.trim() || !form.email.trim() || !form.full_name.trim()) {
      toast.error('Name, username, and email are required.');
      return;
    }
    if (!editingUser && form.password.length < 8) {
      toast.error('New users require a password of at least 8 characters.');
      return;
    }

    try {
      setSubmitting(true);
      if (editingUser) {
        await updateUser(editingUser.id, {
          username: form.username.trim(),
          email: form.email.trim(),
          full_name: form.full_name.trim(),
          role: form.role,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success('User updated successfully.');
      } else {
        const payload: UserCreate = {
          username: form.username.trim(),
          email: form.email.trim(),
          full_name: form.full_name.trim(),
          password: form.password,
          role: form.role,
          district_id: currentUser?.district_id,
          school_id: currentUser?.school_id,
        };
        await createUser(payload);
        toast.success('User created successfully.');
      }
      setModalOpen(false);
      setEditingUser(null);
      setForm(emptyForm);
      await loadUsers();
    } catch (saveError) {
      console.error('Failed to save user:', saveError);
      toast.error(
        editingUser
          ? 'User could not be updated.'
          : 'User could not be created.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUserStatus = async (selectedUser: User) => {
    try {
      setChangingStatusId(selectedUser.id);
      await setUserActive(selectedUser.id, !selectedUser.is_active);
      toast.success(
        `${selectedUser.full_name} ${
          selectedUser.is_active ? 'deactivated' : 'activated'
        }.`
      );
      await loadUsers();
    } catch (statusError) {
      console.error('Failed to change user status:', statusError);
      toast.error('User status could not be changed.');
    } finally {
      setChangingStatusId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Users</h2>
          <p className="text-sm text-gray-600">
            {totalUsers} account{totalUsers === 1 ? '' : 's'} in your accessible
            schools and districts.
          </p>
        </div>
        {canCreate && <Button onClick={openCreate}>Add user</Button>}
      </div>

      <div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 md:grid-cols-3">
        <label className="text-sm font-medium text-gray-700">
          Search
          <input
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            value={search}
            onChange={event => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Name, username, or email"
          />
        </label>
        <label className="text-sm font-medium text-gray-700">
          Role
          <select
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            value={role}
            onChange={event => {
              setRole(event.target.value as UserRole | '');
              setPage(1);
            }}
          >
            <option value="">All roles</option>
            {ROLE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {formatRole(option)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium text-gray-700">
          Status
          <select
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            value={activeFilter}
            onChange={event => {
              setActiveFilter(
                event.target.value as 'all' | 'active' | 'inactive'
              );
              setPage(1);
            }}
          >
            <option value="all">All accounts</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
          {error}{' '}
          <button className="underline" onClick={() => void loadUsers()}>
            Retry
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <p className="p-8 text-center text-gray-600">Loading users…</p>
        ) : users.length === 0 ? (
          <p className="p-8 text-center text-gray-600">
            No users match the selected filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Username', 'Role', 'Status', 'Actions'].map(
                    heading => (
                      <th
                        key={heading}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(account => (
                  <tr key={account.id}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">
                        {account.full_name}
                      </p>
                      <p className="text-sm text-gray-500">{account.email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {account.username}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatRole(account.role)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          account.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {account.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="space-x-2 px-4 py-3">
                      {canUpdate && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(account)}
                          >
                            Edit
                          </Button>
                          {account.id !== currentUser?.id && (
                            <Button
                              size="sm"
                              variant={account.is_active ? 'danger' : 'success'}
                              loading={changingStatusId === account.id}
                              onClick={() => void toggleUserStatus(account)}
                            >
                              {account.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage(current => current - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage(current => current + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingUser ? 'Edit user' : 'Add user'}
        size="lg"
      >
        <form className="space-y-4" onSubmit={submitUser}>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Full name
              <input
                required
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                value={form.full_name}
                onChange={event =>
                  setForm(current => ({
                    ...current,
                    full_name: event.target.value,
                  }))
                }
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Username
              <input
                required
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                value={form.username}
                onChange={event =>
                  setForm(current => ({
                    ...current,
                    username: event.target.value,
                  }))
                }
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Email
              <input
                required
                type="email"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                value={form.email}
                onChange={event =>
                  setForm(current => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Role
              <select
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                value={form.role}
                onChange={event =>
                  setForm(current => ({
                    ...current,
                    role: event.target.value as UserRole,
                  }))
                }
              >
                {assignableRoles.map(option => (
                  <option key={option} value={option}>
                    {formatRole(option)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block text-sm font-medium text-gray-700">
            {editingUser ? 'New password (optional)' : 'Temporary password'}
            <input
              type="password"
              required={!editingUser}
              minLength={8}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
              value={form.password}
              onChange={event =>
                setForm(current => ({
                  ...current,
                  password: event.target.value,
                }))
              }
            />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {editingUser ? 'Save changes' : 'Create user'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UsersPage;
